'use client';

import React, { useEffect, useState } from 'react';
import type { ArticleHeading } from '@/lib/news/newsUtils';
import { cn } from '@/lib/twUtils';

/** Indice dell'articolo: segna la sezione che si sta leggendo. */
const NewsToc = ({ headings }: { headings: ArticleHeading[] }) => {
  const [activeId, setActiveId] = useState<string | null>(headings[0]?.id ?? null);

  useEffect(() => {
    if (headings.length === 0) return;

    /*
     * Attiva è l'ultima sezione il cui titolo ha superato il terzo alto dello
     * schermo. Due scelte che contano:
     * - dalla posizione e non con un IntersectionObserver: un salto (un link
     *   dell'indice, il ritorno da un pannello) scavalca la fascia osservata e
     *   lascerebbe accesa la sezione sbagliata;
     * - i titoli si cercano per id a ogni lettura, senza tenerli da parte: se
     *   il testo si rimonta (l'articolo che arriva in diretta), un riferimento
     *   tenuto dal primo giro punterebbe a un nodo staccato, con `top` a 0, e
     *   si accenderebbe sempre l'ultima voce.
     * Sei `getBoundingClientRect` per evento di scroll non costano nulla:
     * niente `requestAnimationFrame` di mezzo.
     */
    const update = () => {
      const line = window.innerHeight * 0.3;
      let current = headings[0].id;
      for (const heading of headings) {
        const node = document.getElementById(heading.id);
        if (node && node.getBoundingClientRect().top <= line) current = heading.id;
      }
      setActiveId(current);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [headings]);

  if (headings.length < 2) return null;

  return (
    <nav aria-label="In questo articolo">
      <p className="text-[0.6875rem] font-semibold tracking-[0.1em] text-ink-faint uppercase">
        In questo articolo
      </p>
      <ol className="mt-3 flex flex-col border-l border-edge">
        {headings.map(heading => (
          <li key={heading.id}>
            <a
              href={`#${heading.id}`}
              aria-current={activeId === heading.id ? 'location' : undefined}
              className={cn(
                '-ml-px block border-l-2 py-1.5 pl-3.5 text-[0.84375rem] leading-[1.4] transition-colors ds-focus',
                activeId === heading.id
                  ? 'border-accent font-semibold text-primary'
                  : 'border-transparent text-ink-muted hover:text-primary'
              )}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default NewsToc;
